import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  pointerWithin,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type Modifier,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronRight, Folder, FolderPlus, MoreHorizontal } from "lucide-react";
import { MAX_SIDEBAR_FOLDERS, type SidebarFolder } from "@cd/core";
import { api, type DashboardWithMeta } from "../lib/api";
import { canHold, listOf, MINE, moveTo, SHARED, sidebarLayout, type SidebarLayout } from "../lib/sidebarOrder";
import { cn } from "../lib/utils";
import { Menu, MenuContent, MenuItem, MenuTrigger, Popover, PopoverContent, PopoverTrigger } from "../components/ui/overlay";
import { Tip } from "../components/ui/primitives";

/** Drag ids: a folder's header is `folder:<id>`; an empty list's drop zone is `drop:<list>`; a dashboard is its id. */
const FOLDER = "folder:";
const DROP = "drop:";
const CLOSED_KEY = "sidebarClosedFolders";

/** Keeps a dragged row in its column. */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/**
 * Cancels the click that ends a drag. dnd-kit stops that click reaching React, so the router's Link
 * never handles it, but the browser would still follow the link's href and reload the page.
 */
function swallowNextClick() {
  const block = (e: MouseEvent) => e.preventDefault();
  window.addEventListener("click", block, { capture: true, once: true });
  setTimeout(() => window.removeEventListener("click", block, { capture: true }), 100);
}

/** A folder drag only lands on other folders. A dashboard lands on the row under the pointer, else the nearest. */
const collision: CollisionDetection = (args) => {
  const droppableContainers = String(args.active.id).startsWith(FOLDER)
    ? args.droppableContainers.filter((c) => String(c.id).startsWith(FOLDER))
    : args.droppableContainers;
  const hits = pointerWithin({ ...args, droppableContainers });
  return hits.length ? hits : closestCenter({ ...args, droppableContainers });
};

/** Which folders are closed, remembered per browser like the collapsed sidebar. */
function useClosedFolders() {
  const [closed, setClosed] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(CLOSED_KEY) ?? "[]") as string[]);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(CLOSED_KEY, JSON.stringify([...closed]));
    } catch {
      /* private mode */
    }
  }, [closed]);
  const toggle = (id: string) =>
    setClosed((c) => {
      const next = new Set(c);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  return [closed, toggle] as const;
}

/**
 * The dashboard part of the side nav: the viewer's own folders, then Mine, then Shared with me.
 * Dashboards drag within and between them (shares never into Mine, nor their own into Shared with
 * me); dropping one on a folder's header files it at the bottom. Folders drag by their header.
 * Mouse and touch only. Everything is saved for the viewer alone.
 */
export function SidebarNav({ dashboards, collapsed, disabled, showShared, itemClass, renderItem, mineHeader, sharedHeader }: {
  dashboards: DashboardWithMeta[] | undefined;
  /** The icon rail: folders open as a pop-out list, and aren't dragged. */
  collapsed: boolean;
  /** Read-only (viewing as someone without edit on): shown as they have it, nothing moves. */
  disabled: boolean;
  /** Whether to show Shared with me even with nothing outside folders. */
  showShared: boolean;
  itemClass: (active: boolean) => string;
  renderItem: (d: DashboardWithMeta) => ReactNode;
  /** The Mine header, given the new-folder button to place in it (null when there isn't one). */
  mineHeader: (newFolder: ReactNode) => ReactNode;
  sharedHeader: ReactNode;
}) {
  const qc = useQueryClient();
  const folders = useQuery({ queryKey: ["sidebarFolders"], queryFn: api.sidebarFolders });
  const [closed, toggle] = useClosedFolders();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [dragLayout, setDragLayout] = useState<SidebarLayout | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const byId = useMemo(() => new Map((dashboards ?? []).map((d) => [d.id, d])), [dashboards]);
  const folderById = useMemo(() => new Map((folders.data ?? []).map((f) => [f.id, f])), [folders.data]);
  const base = useMemo(() => sidebarLayout(dashboards ?? [], folders.data ?? []), [dashboards, folders.data]);
  const layout = dragLayout ?? base;
  const draggingFolder = activeId?.startsWith(FOLDER) ?? false;

  const refetch = (key: string) => () => qc.invalidateQueries({ queryKey: [key] });
  const saveOrder = useMutation({
    mutationFn: ({ ids, folderId }: { ids: string[]; folderId?: string }) => api.setSidebarOrder(ids, folderId),
    onError: refetch("dashboards"),
  });
  const saveFolderOrder = useMutation({ mutationFn: api.setSidebarFolderOrder, onError: refetch("sidebarFolders") });
  const createFolder = useMutation({
    mutationFn: () => api.createSidebarFolder("New folder"),
    onSuccess: (f) => {
      qc.setQueryData<SidebarFolder[]>(["sidebarFolders"], (fs) => [...(fs ?? []), f]);
      setRenaming(f.id);
    },
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.renameSidebarFolder(id, name),
    onMutate: ({ id, name }) => qc.setQueryData<SidebarFolder[]>(["sidebarFolders"], (fs) => fs?.map((f) => (f.id === id ? { ...f, name } : f))),
    onSettled: refetch("sidebarFolders"),
  });
  const remove = useMutation({
    mutationFn: api.deleteSidebarFolder,
    onSuccess: (_, id) => {
      if (closed.has(id)) toggle(id);
      return Promise.all([refetch("sidebarFolders")(), refetch("dashboards")()]);
    },
  });

  /** Saves one list's order, moving its dashboards into it. */
  const place = (list: string, ids: string[]) => {
    const folderId = list === MINE || list === SHARED ? undefined : list;
    const position = new Map(ids.map((id, i) => [id, i]));
    qc.setQueryData<DashboardWithMeta[]>(["dashboards"], (ds) =>
      ds?.map((d) => (position.has(d.id) ? { ...d, sidebarPosition: position.get(d.id), sidebarFolderId: folderId } : d)),
    );
    saveOrder.mutate({ ids, folderId });
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
    setDragLayout(base);
  };

  // Over a row in another list (or an empty one): it moves there now, so the rows make room for it.
  const onDragOver = ({ active, over }: DragOverEvent) => {
    const id = String(active.id);
    const overId = over && String(over.id);
    if (!overId || id.startsWith(FOLDER) || overId.startsWith(FOLDER)) return;
    setDragLayout((l) => {
      if (!l) return l;
      const to = overId.startsWith(DROP) ? overId.slice(DROP.length) : listOf(l, overId);
      const d = byId.get(id);
      if (!to || to === listOf(l, id) || !d || !canHold(to, d)) return l;
      return moveTo(l, id, to, overId.startsWith(DROP) ? undefined : l.lists[to]!.indexOf(overId));
    });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    swallowNextClick();
    const l = dragLayout;
    setDragLayout(null);
    setActiveId(null);
    const id = String(active.id);
    const overId = over && String(over.id);
    if (!l || !overId) return;

    if (id.startsWith(FOLDER)) {
      if (!overId.startsWith(FOLDER) || overId === id) return;
      const order = arrayMove(l.folders, l.folders.indexOf(id.slice(FOLDER.length)), l.folders.indexOf(overId.slice(FOLDER.length)));
      qc.setQueryData<SidebarFolder[]>(["sidebarFolders"], (fs) => fs?.map((f) => ({ ...f, position: order.indexOf(f.id) })));
      saveFolderOrder.mutate(order);
      return;
    }
    // Dropped on a folder's header: filed at its bottom.
    if (overId.startsWith(FOLDER)) {
      const to = overId.slice(FOLDER.length);
      return place(to, moveTo(l, id, to).lists[to]!);
    }
    const to = overId.startsWith(DROP) ? overId.slice(DROP.length) : listOf(l, overId);
    const list = to ? l.lists[to]! : [];
    if (!to || !list.includes(id)) return;
    const order = overId.startsWith(DROP) ? list : arrayMove(list, list.indexOf(id), list.indexOf(overId));
    if (listOf(base, id) === to && order.every((x, i) => x === base.lists[to]![i])) return;
    place(to, order);
  };

  const onDragCancel = () => {
    swallowNextClick();
    setDragLayout(null);
    setActiveId(null);
  };

  const list = (key: string, placeholder: ReactNode) => (
    <DashboardList ids={layout.lists[key] ?? []} listKey={key} disabled={disabled} placeholder={placeholder}>
      {(id) => renderItem(byId.get(id)!)}
    </DashboardList>
  );
  const dropHint = (text: string) => activeId && !draggingFolder && <p className="px-2 py-1 text-xs text-subtle">{text}</p>;
  const hasOwn = dashboards?.some((d) => !d.sharedBy) ?? true;
  const atLimit = (folders.data?.length ?? 0) >= MAX_SIDEBAR_FOLDERS;
  const newFolder = !collapsed && !disabled && (
    <Tip content={atLimit ? `Up to ${MAX_SIDEBAR_FOLDERS} folders` : "New folder"}>
      <button
        onClick={() => createFolder.mutate()}
        disabled={atLimit || createFolder.isPending}
        className="rounded-md p-0.5 text-subtle hover:bg-surface-2 hover:text-foreground disabled:opacity-40"
        aria-label="New folder"
      >
        <FolderPlus className="size-3.5" />
      </button>
    </Tip>
  );
  const active = activeId && (activeId.startsWith(FOLDER) ? folderById.get(activeId.slice(FOLDER.length)) : byId.get(activeId));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      {layout.folders.length > 0 &&
        (collapsed ? (
          <div className="space-y-0.5 border-t border-border pt-2">
            {layout.folders.map((id) => (
              <RailFolder key={id} name={folderById.get(id)?.name ?? ""} itemClass={itemClass}>
                {(layout.lists[id] ?? []).map((x) => byId.get(x)!)}
              </RailFolder>
            ))}
          </div>
        ) : (
          <div className="space-y-0.5">
            <SortableContext items={layout.folders.map((id) => FOLDER + id)} strategy={verticalListSortingStrategy} disabled={disabled || !!renaming}>
              {layout.folders.map((id) => {
                const folder = folderById.get(id);
                if (!folder) return null;
                const open = !closed.has(id) && !draggingFolder;
                return (
                  <div key={id}>
                    <FolderHeader
                      folder={folder}
                      open={open}
                      disabled={disabled}
                      renaming={renaming === id}
                      itemClass={itemClass}
                      onToggle={() => toggle(id)}
                      onStartRename={() => setRenaming(id)}
                      onRenamed={(name) => {
                        setRenaming(null);
                        if (name && name !== folder.name) rename.mutate({ id, name });
                      }}
                      onRemove={() => remove.mutate(id)}
                    />
                    {open && (
                      <div className="ml-3.5 border-l border-border pl-1.5">
                        {list(id, <p className="px-2 py-1 text-xs text-subtle">{disabled ? "Empty" : "Empty. Drag dashboards here."}</p>)}
                      </div>
                    )}
                  </div>
                );
              })}
            </SortableContext>
          </div>
        ))}
      <div>
        {mineHeader(newFolder || null)}
        {list(MINE, !hasOwn ? !collapsed && <p className="px-2 py-1 text-xs text-subtle">No dashboards yet.</p> : dropHint("Drop here to take it out of its folder."))}
      </div>
      {showShared && (
        <div>
          {sharedHeader}
          {list(SHARED, dropHint("Drop here to take it out of its folder."))}
        </div>
      )}
      <DragOverlay>
        {active &&
          ("title" in active ? (
            <div className="rounded-lg bg-surface shadow-md">{renderItem(active)}</div>
          ) : (
            <div className={cn(itemClass(false), "bg-surface shadow-md")}>
              <ChevronRight className="size-3.5 shrink-0 opacity-60" />
              <Folder className="size-4 shrink-0 opacity-70" />
              <span className="truncate">{active.name}</span>
            </div>
          ))}
      </DragOverlay>
    </DndContext>
  );
}

/** One list of dashboards, sortable. When empty, a drop zone showing `placeholder`. */
function DashboardList({ ids, listKey, disabled, placeholder, children }: {
  ids: string[];
  listKey: string;
  disabled: boolean;
  placeholder: ReactNode;
  children: (id: string) => ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: DROP + listKey, disabled: disabled || ids.length > 0 });
  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy} disabled={disabled}>
      {ids.map((id) => <SortableRow key={id} id={id}>{children(id)}</SortableRow>)}
      {!ids.length && <div ref={setNodeRef}>{placeholder}</div>}
    </SortableContext>
  );
}

function SortableRow({ id, children }: { id: string; children: ReactNode }) {
  const { setNodeRef, listeners, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} className={cn(isDragging && "opacity-40")} {...listeners}>
      {children}
    </div>
  );
}

/** A folder's row: open or close it, drag it by the row, drop a dashboard on it, or rename and remove it from its menu. */
function FolderHeader({ folder, open, disabled, renaming, itemClass, onToggle, onStartRename, onRenamed, onRemove }: {
  folder: SidebarFolder;
  open: boolean;
  disabled: boolean;
  renaming: boolean;
  itemClass: (active: boolean) => string;
  onToggle: () => void;
  onStartRename: () => void;
  onRenamed: (name: string | null) => void;
  onRemove: () => void;
}) {
  const { setNodeRef, listeners, transform, transition, isDragging, isOver, active } = useSortable({ id: FOLDER + folder.id });
  // Renaming from the menu: keep focus in the input instead of handing it back to the menu button.
  const toRename = useRef(false);
  const dropping = isOver && !!active && !String(active.id).startsWith(FOLDER);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn("group", itemClass(false), "pr-1", dropping && "bg-accent-soft text-foreground ring-1 ring-accent", isDragging && "opacity-40")}
      {...listeners}
    >
      {renaming ? (
        <>
          <Folder className="size-4 shrink-0 opacity-70" />
          <RenameInput initial={folder.name} onDone={onRenamed} />
        </>
      ) : (
        <>
          <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={open}>
            <ChevronRight className={cn("size-3.5 shrink-0 opacity-60 transition-transform", open && "rotate-90")} />
            <Folder className="size-4 shrink-0 opacity-70" />
            <span className="truncate">{folder.name}</span>
          </button>
          {!disabled && (
            <Menu>
              <MenuTrigger asChild>
                <button
                  className="rounded-md p-0.5 text-subtle opacity-0 hover:bg-surface hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
                  aria-label={`${folder.name} options`}
                >
                  <MoreHorizontal className="size-3.5" />
                </button>
              </MenuTrigger>
              <MenuContent
                align="start"
                onCloseAutoFocus={(e) => {
                  if (toRename.current) e.preventDefault();
                  toRename.current = false;
                }}
              >
                <MenuItem
                  onSelect={() => {
                    toRename.current = true;
                    onStartRename();
                  }}
                >
                  Rename
                </MenuItem>
                <MenuItem danger onSelect={onRemove}>Remove folder</MenuItem>
              </MenuContent>
            </Menu>
          )}
        </>
      )}
    </div>
  );
}

/** Enter or leaving the field saves; Escape (or an empty name) keeps the old one. */
function RenameInput({ initial, onDone }: { initial: string; onDone: (name: string | null) => void }) {
  const [value, setValue] = useState(initial);
  const done = useRef(false);
  const finish = (name: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(name);
  };
  return (
    <input
      autoFocus
      value={value}
      maxLength={60}
      aria-label="Folder name"
      onChange={(e) => setValue(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(value.trim() || null);
        if (e.key === "Escape") finish(null);
      }}
      onBlur={() => finish(value.trim() || null)}
      className="min-w-0 flex-1 rounded-md border border-border bg-surface px-1.5 py-0.5 text-sm text-foreground outline-none focus:border-accent"
    />
  );
}

/** On the icon rail: a folder icon that opens its dashboards beside it. */
function RailFolder({ name, itemClass, children: ds }: { name: string; itemClass: (active: boolean) => string; children: DashboardWithMeta[] }) {
  const [open, setOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const here = ds.some((d) => path === `/d/${d.id}`);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tip content={name} side="right">
        <PopoverTrigger asChild>
          <button className={cn(itemClass(here), "w-full")} aria-label={name}>
            <Folder className="size-4 shrink-0 opacity-70" />
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent side="right" className="w-56">
        <div className="truncate px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">{name}</div>
        {ds.map((d) => (
          <Link
            key={d.id}
            to="/d/$id"
            params={{ id: d.id }}
            onClick={() => setOpen(false)}
            className={cn(
              "block truncate rounded-lg px-2 py-1.5 text-sm",
              path === `/d/${d.id}` ? "bg-surface-2 font-medium text-foreground" : "text-muted hover:bg-surface-2 hover:text-foreground",
            )}
          >
            {d.title}
          </Link>
        ))}
        {!ds.length && <p className="px-2 py-1 text-xs text-subtle">Empty</p>}
      </PopoverContent>
    </Popover>
  );
}
